// API client for MongoDB backend
// Helper to get API URL - ensures /api is appended and handles production
import { getApiUrl } from './apiUrl';
const API_BASE_URL = getApiUrl();

// Types matching your MongoDB models
export interface Property {
  _id: string;
  title: string;
  description: string;
  price: number;
  location: string;
  images: string[];
  bedrooms: number;
  bathrooms: number;
  area: number;
  propertyType: string;
  status: 'available' | 'rented' | 'maintenance';
  isVerified: boolean;
  landlordId: string;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  _id: string;
  email: string;
  role: 'CLIENT' | 'LANDLORD' | 'ADMIN';
  isVerified: boolean;
  profileData?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    avatar?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Application {
  _id: string;
  propertyId: string;
  clientId: string;
  status: 'pending' | 'approved' | 'rejected';
  appliedAt: string;
  reviewedAt?: string;
}

export interface Payment {
  _id: string;
  applicationId: string;
  amount: number;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
}

export interface Message {
  _id: string;
  applicationId: string;
  senderId: string;
  content: string;
  timestamp: string;
}

// API Response types
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Generic API call function
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: controller.signal,
      ...options,
    });

    clearTimeout(timeoutId);

    // Check if response is ok before trying to parse JSON
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If JSON parsing fails, use the status text or default message
        errorMessage = response.statusText || errorMessage;
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }

    // Check Content-Type to handle CSV/text responses
    const contentType = response.headers.get('content-type') || '';
    let data;
    
    if (contentType.includes('text/csv') || contentType.includes('text/plain')) {
      // Handle CSV/text responses
      data = await response.text();
    } else {
      // Handle JSON responses (default)
      data = await response.json();
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    let errorMessage = 'Unknown error occurred';
    
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        errorMessage = 'Request timeout - backend server may not be running';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error - unable to connect to backend server';
      } else {
        errorMessage = error.message;
      }
    }
    
    return {
      success: false,
      error: errorMessage,
    };
  }
}

// Property API functions
export const propertyApi = {
  // Get all properties with optional filters
  async getProperties(filters?: {
    verified?: boolean;
    limit?: number;
    search?: string;
    propertyType?: string;
    minPrice?: number;
    maxPrice?: number;
  }): Promise<ApiResponse<{ properties: Property[] }>> {
    const params = new URLSearchParams();
    
    if (filters?.verified !== undefined) {
      params.append('verified', filters.verified.toString());
    }
    if (filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.propertyType) {
      params.append('propertyType', filters.propertyType);
    }
    if (filters?.minPrice) {
      params.append('minPrice', filters.minPrice.toString());
    }
    if (filters?.maxPrice) {
      params.append('maxPrice', filters.maxPrice.toString());
    }

    // Always include verified=true for client-facing property searches if not already specified
    if (filters?.verified === undefined) {
      params.append('verified', 'true');
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/properties?${queryString}` : '/properties?verified=true';
    
    return apiCall<{ properties: Property[] }>(endpoint);
  },

  // Get single property by ID
  async getProperty(id: string): Promise<ApiResponse<Property>> {
    return apiCall<Property>(`/properties/${id}`);
  },

  // Create new property (requires authentication)
  async createProperty(property: Omit<Property, '_id' | 'createdAt' | 'updatedAt'>, token: string): Promise<ApiResponse<Property>> {
    return apiCall<Property>('/properties', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(property),
    });
  },

  // Update property (requires authentication)
  async updateProperty(id: string, property: Partial<Property>, token: string): Promise<ApiResponse<Property>> {
    return apiCall<Property>(`/properties/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(property),
    });
  },

  // Delete property (requires authentication)
  async deleteProperty(id: string, token: string): Promise<ApiResponse<void>> {
    return apiCall<void>(`/properties/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Auth API functions
export const authApi = {
  // Login user
  async login(email: string, password: string): Promise<ApiResponse<{ user: User; token: string }>> {
    return apiCall<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  // Register user
  async register(userData: {
    email: string;
    password: string;
    role: 'CLIENT' | 'LANDLORD';
  }): Promise<ApiResponse<{ user: User; token: string }>> {
    return apiCall<{ user: User; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  // Get current user
  async getCurrentUser(token: string): Promise<ApiResponse<User>> {
    return apiCall<User>('/auth/me', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Logout user
  async logout(token: string): Promise<ApiResponse<void>> {
    return apiCall<void>('/auth/logout', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Application API functions
export const applicationApi = {
  // Get applications for a user
  async getApplications(token: string): Promise<ApiResponse<{ applications: Application[] }>> {
    return apiCall<{ applications: Application[] }>('/applications', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Create new application
  async createApplication(application: Omit<Application, '_id' | 'appliedAt'>, token: string): Promise<ApiResponse<Application>> {
    return apiCall<Application>('/applications', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(application),
    });
  },

  // Update application status
  async updateApplicationStatus(id: string, status: Application['status'], token: string): Promise<ApiResponse<Application>> {
    return apiCall<Application>(`/applications/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });
  },
};

// Notification API functions
export interface Notification {
  _id: string;
  id: string;
  user: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  is_read: boolean;
  readAt?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  relatedEntity?: {
    type: string;
    id: string;
  };
  actionUrl?: string;
  createdAt: string;
  created_at: string;
}

export const notificationApi = {
  // Get notifications for current user
  async getNotifications(
    token: string,
    options?: { page?: number; limit?: number; unreadOnly?: boolean; type?: string }
  ): Promise<ApiResponse<{ notifications: Notification[]; pagination: any; unreadCount: number }>> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.unreadOnly) params.append('unreadOnly', 'true');
    if (options?.type) params.append('type', options.type);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/notifications?${queryString}` : '/notifications';
    
    return apiCall<{ notifications: Notification[]; pagination: any; unreadCount: number }>(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get unread notification count
  async getUnreadCount(token: string): Promise<ApiResponse<{ unreadCount: number }>> {
    return apiCall<{ unreadCount: number }>('/notifications/unread-count', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Mark notification as read
  async markAsRead(notificationId: string, token: string): Promise<ApiResponse<Notification>> {
    return apiCall<Notification>(`/notifications/${notificationId}/read`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Mark all notifications as read
  async markAllAsRead(token: string): Promise<ApiResponse<{ message: string; updatedCount: number }>> {
    return apiCall<{ message: string; updatedCount: number }>('/notifications/read-all', {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Delete notification
  async deleteNotification(notificationId: string, token: string): Promise<ApiResponse<void>> {
    return apiCall<void>(`/notifications/${notificationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

export default {
  property: propertyApi,
  auth: authApi,
  application: applicationApi,
  notification: notificationApi,
};

// Import types from types file  
import type { 
  LandlordAccount, 
  PayoutRequest, 
  PlatformSettings, 
  EarningsBreakdown, 
  CommissionHistoryEntry 
} from '@/types';

// Landlord Account API functions
export const landlordAccountApi = {
  // Get account balance with commission breakdown
  async getBalance(token: string): Promise<ApiResponse<LandlordAccount>> {
    return apiCall<LandlordAccount>('/landlord-accounts/balance', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get earnings history with commission breakdown
  async getEarnings(token: string, params?: { startDate?: string; endDate?: string; limit?: number; page?: number }): Promise<ApiResponse<{
    breakdown: EarningsBreakdown;
    earnings: any[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiCall(`/landlord-accounts/earnings${queryString}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get account statement
  async getStatement(token: string, params?: { startDate?: string; endDate?: string }): Promise<ApiResponse<{
    period: { startDate?: string; endDate?: string };
    summary: EarningsBreakdown;
    transactions: any[];
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiCall(`/landlord-accounts/statement${queryString}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Payout API functions
export const payoutApi = {
  // Create payout request
  async createRequest(
    token: string,
    data: {
      amount: number;
      paymentMethod: 'stripe_connect' | 'bank_transfer';
      bankDetails?: any;
      stripeAccountId?: string;
    }
  ): Promise<ApiResponse<{ payoutRequest: PayoutRequest }>> {
    return apiCall<{ payoutRequest: PayoutRequest }>('/payouts/request', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
  },

  // Get landlord's payout requests
  async getRequests(token: string, status?: string): Promise<ApiResponse<{ payoutRequests: PayoutRequest[] }>> {
    const queryString = status ? `?status=${encodeURIComponent(status)}` : '';
    return apiCall<{ payoutRequests: PayoutRequest[] }>(`/payouts/requests${queryString}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get specific payout request
  async getRequest(token: string, id: string): Promise<ApiResponse<{ payoutRequest: PayoutRequest }>> {
    return apiCall<{ payoutRequest: PayoutRequest }>(`/payouts/requests/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Cancel payout request
  async cancelRequest(token: string, id: string): Promise<ApiResponse<void>> {
    return apiCall<void>(`/payouts/requests/${id}/cancel`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Admin Payout API functions
export const adminPayoutApi = {
  // Get pending payouts
  async getPending(token: string): Promise<ApiResponse<{ payoutRequests: PayoutRequest[] }>> {
    return apiCall<{ payoutRequests: PayoutRequest[] }>('/payouts/admin/pending', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Approve payout request
  async approve(token: string, id: string, notes?: string): Promise<ApiResponse<{ payoutRequest: PayoutRequest }>> {
    return apiCall<{ payoutRequest: PayoutRequest }>(`/payouts/admin/${id}/approve`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ notes }),
    });
  },

  // Reject payout request
  async reject(token: string, id: string, reason: string, notes?: string): Promise<ApiResponse<{ payoutRequest: PayoutRequest }>> {
    return apiCall<{ payoutRequest: PayoutRequest }>(`/payouts/admin/${id}/reject`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ reason, notes }),
    });
  },

  // Process payout
  async process(token: string, id: string, transferId?: string): Promise<ApiResponse<{ payoutRequest: PayoutRequest; transferId?: string }>> {
    return apiCall<{ payoutRequest: PayoutRequest; transferId?: string }>(`/payouts/admin/${id}/process`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ transferId }),
    });
  },

  // Get payout history
  async getHistory(
    token: string,
    params?: { status?: string; landlordId?: string; startDate?: string; endDate?: string; limit?: number; page?: number }
  ): Promise<ApiResponse<{
    payoutRequests: PayoutRequest[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.landlordId) queryParams.append('landlordId', params.landlordId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiCall(`/payouts/admin/history${queryString}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Commission API functions
export const commissionApi = {
  // Get current commission rate
  async getRate(token: string): Promise<ApiResponse<PlatformSettings>> {
    return apiCall<PlatformSettings>('/admin/commission/rate', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Update commission rate
  async updateRate(token: string, rate: number, reason: string): Promise<ApiResponse<{
    oldRate: number;
    newRate: number;
    effectiveFrom: string;
    reason: string;
  }>> {
    return apiCall('/admin/commission/rate', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ rate, reason }),
    });
  },

  // Get commission history
  async getHistory(token: string, params?: { startDate?: string; endDate?: string }): Promise<ApiResponse<{ history: CommissionHistoryEntry[] }>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiCall<{ history: CommissionHistoryEntry[] }>(`/admin/commission/history${queryString}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get commission statistics
  async getStats(token: string, params?: { startDate?: string; endDate?: string }): Promise<ApiResponse<{
    totalCommission: number;
    commissionByLandlord: any[];
    monthlyBreakdown: any[];
    period: { startDate?: string; endDate?: string };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiCall(`/admin/commission/stats${queryString}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // Get commission report
  async getReport(token: string, params?: { startDate?: string; endDate?: string; format?: 'json' | 'csv' }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.format) queryParams.append('format', params.format);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiCall(`/admin/commission/report${queryString}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },
};

// Platform Settings API functions
export const platformSettingsApi = {
  // Get current platform settings (admin)
  async get(token: string): Promise<ApiResponse<PlatformSettings>> {
    return apiCall<{ settings: PlatformSettings }>('/admin/platform-settings', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }).then((res) => {
      if (!res.success) return res as any;
      return { ...res, data: (res.data as any)?.settings } as any;
    });
  },

  // Update platform settings (admin) - commission rate is handled by commissionApi.updateRate
  async update(token: string, updates: Partial<PlatformSettings>): Promise<ApiResponse<PlatformSettings>> {
    return apiCall<{ settings: PlatformSettings }>('/admin/platform-settings', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    }).then((res) => {
      if (!res.success) return res as any;
      return { ...res, data: (res.data as any)?.settings } as any;
    });
  },
};